/**
 * OtpInput — React (web) implementation.
 *
 * One visually-hidden input owns the value, focus, and paste; the visible cells
 * are a presentation overlay. All styling references the shared token constants
 * in OtpInput.ts. No raw hex values or pixel literals in this file.
 */

import React from 'react';
import { colors } from '../../elements/colors';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';
import { Icon } from '../Icon/Icon.web';
import {
  sanitizeOtpDigits,
  isOtpComplete,
  activeOtpIndex,
  otpCellBorderColor,
  OTP_CELL_HEIGHT,
  OTP_CELL_RADIUS,
  OTP_CELL_BORDER_WIDTH,
  OTP_CELL_GAP,
  OTP_CELL_BACKGROUND,
  OTP_CELL_TEXT_COLOR,
  OTP_CELL_TYPOGRAPHY,
} from './OtpInput';

// Web-only states: Verifying (the code was sent and is being checked) and an
// error message under the cells. Native draws neither.
const WEB_OTP_VERIFYING_BACKGROUND = colors.background.secondary;
const WEB_OTP_VERIFYING_BORDER = colors.stroke.divider;
const WEB_OTP_VERIFYING_TEXT_COLOR = colors.text.tertiary;
const WEB_OTP_MESSAGE_GAP = spacing.s;
const WEB_OTP_MESSAGE_TYPOGRAPHY = typography.c1Medium;
const WEB_OTP_VERIFYING_MESSAGE_COLOR = colors.text.secondary;
const WEB_OTP_ERROR_MESSAGE_COLOR = colors.text.alert;
const WEB_OTP_ERROR_ICON_SIZE = sizing.icon.xs;

// Web draws a resting cell (not the caret cell, not in error) in the
// field-border stroke, so the cell edge meets the 3:1 non-text contrast minimum
// on white. The shared border colour keeps the lighter divider because native
// reads it too.
const WEB_OTP_RESTING_BORDER = colors.stroke.input;

function webOtpCellBorderColor(active: boolean, error: boolean): string {
  return active || error ? otpCellBorderColor(active, error) : WEB_OTP_RESTING_BORDER;
}

const OTP_FOCUS_STYLE_ID = 'galapagos-otp-input-focus-visible';
const OTP_ROOT_SELECTOR = '[data-galapagos-otp-input="true"]';

// The real input is transparent, so its own outline would be invisible. Ring
// the cell at the caret instead, only while the input has keyboard focus.
function ensureOtpFocusStyle(): void {
  ensureStyle(
    OTP_FOCUS_STYLE_ID,
    `@supports selector(:has(*)) { ${OTP_ROOT_SELECTOR}:has(input:focus-visible) [data-otp-active="true"] { ${FOCUS_RING_OUTLINE} } }`,
  );
}

export interface WebOtpInputProps {
  length?: number;
  value?: string;
  disabled?: boolean;
  error?: boolean;
  /** Web-only: the message under the cells while `error` is set. */
  errorText?: string;
  /** Web-only: the full code is being checked; input is locked and cells grey out. */
  verifying?: boolean;
  /** Web-only: announced and shown under the cells while `verifying`. */
  verifyingText?: string;
  onChange?: (next: string) => void;
  onComplete?: (code: string) => void;
  completionAnnouncement?: string;
  /** Form-field `name` for browser autofill heuristics; defaults to the
   *  `data-testid`, then to the autofill purpose (`one-time-code`). */
  name?: string;
  'data-testid'?: string;
  accessibilityLabel?: string;
}

export function OtpInput({
  length = 6,
  value = '',
  disabled = false,
  error = false,
  errorText,
  verifying = false,
  verifyingText,
  onChange,
  onComplete,
  completionAnnouncement,
  name,
  'data-testid': dataTestId,
  accessibilityLabel,
}: WebOtpInputProps) {
  ensureOtpFocusStyle();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const messageId = React.useId();
  const [focused, setFocused] = React.useState(false);
  // The last completed code, not a boolean — replacing one full code with
  // another in a single edit/paste still differs from this and re-fires.
  const lastCompletedRef = React.useRef<string | null>(null);

  const sanitized = sanitizeOtpDigits(value, length);
  // The highlighted cell follows the caret, so moving it with Home or the
  // arrow keys moves the highlight; before any caret event it is the next digit.
  const [caret, setCaret] = React.useState<number | null>(null);
  const activeIndex = caret === null ? activeOtpIndex(value, length) : Math.min(caret, length - 1);
  const announced = isOtpComplete(sanitized, length) ? completionAnnouncement : undefined;
  const showError = error && !verifying && !!errorText;
  // The one always-mounted polite region reads out completion, then the
  // verifying text, then the error if the check fails.
  const liveMessage = verifying ? verifyingText : showError ? errorText : announced;

  const handleChange = (raw: string) => {
    const next = sanitizeOtpDigits(raw, length);
    onChange?.(next);
    if (isOtpComplete(next, length)) {
      if (lastCompletedRef.current !== next) {
        lastCompletedRef.current = next;
        onComplete?.(next);
      }
    } else {
      lastCompletedRef.current = null;
    }
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: OTP_CELL_GAP,
  };

  const messageStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: WEB_OTP_MESSAGE_GAP,
    fontFamily: WEB_OTP_MESSAGE_TYPOGRAPHY.fontFamily,
    fontWeight: WEB_OTP_MESSAGE_TYPOGRAPHY.fontWeight,
    fontSize: WEB_OTP_MESSAGE_TYPOGRAPHY.fontSize,
    lineHeight: `${WEB_OTP_MESSAGE_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: WEB_OTP_MESSAGE_TYPOGRAPHY.letterSpacing,
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: WEB_OTP_MESSAGE_GAP }}
      data-galapagos-otp-input="true"
    >
      {/* The cells draw the code's digits, so session replay must not record them. */}
      {/* analytics-exempt: forwarded-wrapper */}
      <div style={containerStyle} onClick={() => inputRef.current?.focus()} data-ph-no-capture>
        {Array.from({ length }, (_, i) => {
          const isActive = focused && i === activeIndex;
          const cellStyle: React.CSSProperties = {
            flex: 1,
            height: OTP_CELL_HEIGHT,
            borderRadius: OTP_CELL_RADIUS,
            borderWidth: OTP_CELL_BORDER_WIDTH,
            borderStyle: 'solid',
            borderColor: verifying
              ? WEB_OTP_VERIFYING_BORDER
              : webOtpCellBorderColor(isActive, error),
            backgroundColor: verifying ? WEB_OTP_VERIFYING_BACKGROUND : OTP_CELL_BACKGROUND,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: OTP_CELL_TYPOGRAPHY.fontFamily,
            fontWeight: OTP_CELL_TYPOGRAPHY.fontWeight,
            fontSize: OTP_CELL_TYPOGRAPHY.fontSize,
            lineHeight: `${OTP_CELL_TYPOGRAPHY.lineHeight}px`,
            letterSpacing: OTP_CELL_TYPOGRAPHY.letterSpacing,
            color: verifying ? WEB_OTP_VERIFYING_TEXT_COLOR : OTP_CELL_TEXT_COLOR,
          };
          return (
            <span
              key={i}
              style={cellStyle}
              aria-hidden="true"
              data-otp-active={isActive || undefined}
            >
              {sanitized[i] ?? ''}
            </span>
          );
        })}
        <input
          data-analytics-action="ui.otp-input.change"
          data-ph-no-capture
          ref={inputRef}
          name={name ?? dataTestId ?? 'one-time-code'}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={sanitized}
          disabled={disabled}
          // Verifying locks the field without `disabled`, which would drop focus
          // to the page and strand a keyboard user if the check fails.
          readOnly={verifying}
          aria-disabled={verifying || undefined}
          aria-label={accessibilityLabel}
          aria-invalid={(error && !verifying) || undefined}
          aria-describedby={showError ? messageId : undefined}
          onChange={(e) => {
            if (!verifying) handleChange(e.target.value);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setCaret(null);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          data-testid={dataTestId}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            border: 'none',
            background: 'transparent',
          }}
        />
        <span
          role="status"
          data-ph-no-capture
          aria-live="polite"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {liveMessage}
        </span>
      </div>
      {verifying && verifyingText ? (
        <div aria-hidden style={{ ...messageStyle, color: WEB_OTP_VERIFYING_MESSAGE_COLOR }}>
          {verifyingText}
        </div>
      ) : null}
      {showError ? (
        // Hidden from browsing (the live region already reads it out); still
        // the input's description through aria-describedby.
        <div
          id={messageId}
          aria-hidden
          style={{ ...messageStyle, color: WEB_OTP_ERROR_MESSAGE_COLOR }}
        >
          <Icon
            name="alert"
            size={WEB_OTP_ERROR_ICON_SIZE}
            color={WEB_OTP_ERROR_MESSAGE_COLOR}
            aria-hidden
          />
          <span>{errorText}</span>
        </div>
      ) : null}
    </div>
  );
}
